import React, { useState, useEffect } from 'react';
import { 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Box,
  Typography,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  Switch,
  ListItemSecondaryAction,
  useMediaQuery,
  AppBar,
  Toolbar,
  IconButton,
  useTheme as useMuiTheme,
  SwipeableDrawer
} from '@mui/material';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import LogoutIcon from '@mui/icons-material/Logout';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import PaymentIcon from '@mui/icons-material/Payment';
import PhoneIcon from '@mui/icons-material/Phone';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CallIcon from '@mui/icons-material/Call';
import CloudIcon from '@mui/icons-material/Cloud';
import EventIcon from '@mui/icons-material/Event';
import ContactsIcon from '@mui/icons-material/Contacts';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CampaignIcon from '@mui/icons-material/Campaign';
import ShareIcon from '@mui/icons-material/Share';
import BusinessIcon from '@mui/icons-material/Business';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ExtensionIcon from '@mui/icons-material/Extension';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import logo from '../assets/receptify_logo.png';
import { motion } from 'framer-motion';
import { alpha } from '@mui/material/styles';

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { mode, toggleColorMode } = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [assignedAgents, setAssignedAgents] = useState([]);
  const [createAgentDialogOpen, setCreateAgentDialogOpen] = useState(false);
  
  // Add responsive states
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const isTablet = useMediaQuery(muiTheme.breakpoints.between('sm', 'md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(isTablet);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  useEffect(() => {
    setCollapsed(isTablet);
  }, [isTablet]);

  useEffect(() => {
    if (user?.id) {
      fetchAssignedAgents();
    }
  }, [user]);

  const fetchAssignedAgents = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/${user.id}/agents`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAssignedAgents(data.agents);
      }
    } catch (error) {
      console.error('Error fetching assigned agents:', error);
    }
  };

  const handleProfileClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    logout();
    navigate('/login');
  };

  const handleCreateAgent = () => {
    navigate('/agents/new');
    if (isMobile) setMobileOpen(false);
  };

  const handleMenuClick = (path) => {
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };

  const menuItems = [
    { 
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard',
      showAlways: true
    },
    { 
      text: 'Agents',
      icon: <RecordVoiceOverIcon />,
      path: '/agents',
      showAlways: true
    },
    { 
      text: 'Phone Numbers',
      icon: <PhoneIcon />,
      path: '/phone-numbers',
      showAlways: true
    },
    {
      text: 'Clients',
      icon: <BusinessIcon />,
      path: '/clients',
      showFor: ['admin', 'agency']
    },
    { 
      text: 'Create Agent',
      icon: <AddCircleOutlineIcon />,
      onClick: handleCreateAgent,
      showAlways: true
    },
    { 
      text: 'Plug & Play',
      icon: <ExtensionIcon />,
      path: '/plug-and-play',
      showAlways: true
    },
    { 
      text: 'Recordings',
      icon: <AudiotrackIcon />,
      path: '/recordings',
      showAlways: true
    },
    { 
      text: 'Billing',
      icon: <PaymentIcon />,
      path: '/billing',
      showAlways: true
    },
    {
      text: 'Leads',
      icon: <ContactsIcon />,
      path: '/leads',
      showAlways: true
    },
    {
      text: 'Campaigns',
      icon: <CampaignIcon />,
      path: '/campaigns',
      showAlways: true
    },
    { 
      text: 'Settings',
      icon: <SettingsIcon />,
      path: '/settings',
      showAlways: true
    }
  ];

  const sidebarVariants = {
    hidden: {
      x: -200,
      opacity: 0
    },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 20,
        staggerChildren: 0.05,
        delayChildren: 0.2
      }
    }
  };

  const menuItemVariants = {
    hidden: {
      x: -20,
      opacity: 0
    },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 20
      }
    }
  };

  // Render the drawer content
  const drawerContent = (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={sidebarVariants}
      style={{ 
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box 
        component={motion.div}
        variants={menuItemVariants}
        sx={{ 
          p: 2,
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: collapsed ? 'center' : 'flex-start',
          borderBottom: '1px solid',
          borderColor: 'rgba(0, 0, 0, 0.06)',
          mb: 1
        }}
      >
        <img 
          src={logo} 
          alt="AI Force" 
          style={{ 
            height: '32px',
            objectFit: 'contain',
            filter: 'brightness(0.95)'
          }} 
        />
      </Box>
      
      {/* Account Type Indicator - show only when not collapsed */}
      {user?.role && !collapsed && (
        <Box 
          sx={{ 
            mx: 2, 
            mb: 2,
            mt: 0.5,
            py: 0.75,
            px: 1.5,
            borderRadius: 2,
            backgroundColor: user.role === 'admin' 
              ? mode === 'dark' ? 'rgba(103, 58, 183, 0.2)' : 'rgba(103, 58, 183, 0.1)' 
              : user.role === 'agency' 
                ? mode === 'dark' ? 'rgba(0, 150, 136, 0.2)' : 'rgba(0, 150, 136, 0.1)' 
                : mode === 'dark' ? 'rgba(3, 169, 244, 0.2)' : 'rgba(3, 169, 244, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Box 
            sx={{ 
              width: 8, 
              height: 8, 
              borderRadius: '50%', 
              backgroundColor: user.role === 'admin' 
                ? '#673AB7' 
                : user.role === 'agency' 
                  ? '#009688' 
                  : '#03A9F4',
              mr: 1
            }} 
          />
          <Typography 
            variant="caption" 
            fontWeight={600}
            textTransform="uppercase"
            color={user.role === 'admin' 
              ? mode === 'dark' ? '#B39DDB' : '#673AB7' 
              : user.role === 'agency' 
                ? mode === 'dark' ? '#80CBC4' : '#009688' 
                : mode === 'dark' ? '#81D4FA' : '#03A9F4'
            }
          >
            {user.role === 'admin' ? 'Admin Account' : user.role === 'agency' ? 'Agency Account' : 'Client Account'}
          </Typography>
        </Box>
      )}
      
      <List sx={{ px: collapsed ? 0.5 : 1.5, py: 2, flex: 1 }}>
        {menuItems
          .filter(item => (
            item.showAlways || 
            (user?.role === 'admin' && item.adminOnly) ||
            (item.showFor && item.showFor.includes(user?.role))
          ))
          .map((item) => (
            <motion.div
              key={item.text}
              variants={menuItemVariants}
              whileHover={{ scale: 1.02 }}
            >
              <ListItem
                button
                onClick={item.onClick || (() => handleMenuClick(item.path))}
                selected={location.pathname === item.path}
                sx={{
                  py: 1,
                  minHeight: 36,
                  borderRadius: 1.5,
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  '&.Mui-selected': {
                    bgcolor: user?.role === 'admin' 
                      ? mode === 'dark' ? 'rgba(103, 58, 183, 0.2)' : 'rgba(103, 58, 183, 0.08)'
                      : user?.role === 'agency'
                        ? mode === 'dark' ? 'rgba(0, 150, 136, 0.2)' : 'rgba(0, 150, 136, 0.08)'
                        : mode === 'dark' ? 'rgba(10, 132, 255, 0.2)' : 'rgba(10, 132, 255, 0.08)',
                  },
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    color: location.pathname === item.path 
                      ? user?.role === 'admin' 
                        ? mode === 'dark' ? '#B39DDB' : '#673AB7'
                        : user?.role === 'agency' 
                          ? mode === 'dark' ? '#80CBC4' : '#009688' 
                          : mode === 'dark' ? '#81D4FA' : 'primary.main'
                      : mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary',
                    minWidth: collapsed ? 'auto' : 32,
                    marginRight: collapsed ? 0 : 2,
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    opacity: location.pathname === item.path ? 1 : 0.7,
                    '& .MuiSvgIcon-root': {
                      fontSize: '1.2rem'
                    }
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!collapsed && (
                  <ListItemText 
                    primary={item.text}
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: location.pathname === item.path ? 600 : 500,
                      color: location.pathname === item.path 
                        ? user?.role === 'admin' 
                          ? mode === 'dark' ? '#B39DDB' : '#673AB7'
                          : user?.role === 'agency' 
                            ? mode === 'dark' ? '#80CBC4' : '#009688' 
                            : mode === 'dark' ? '#81D4FA' : 'primary.main'
                        : mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'text.primary',
                      fontSize: '0.875rem'
                    }}
                  />
                )}
              </ListItem>
            </motion.div>
          ))}
        
        {/* Keep User Management for admin only */}
        {user?.role === 'admin' && (
          <motion.div
            variants={menuItemVariants}
            whileHover={{ scale: 1.02 }}
          >
            <ListItem
              button
              onClick={() => handleMenuClick('/user-management')}
              selected={location.pathname === '/user-management'}
              sx={{
                py: 1,
                minHeight: 36,
                borderRadius: 1.5,
                justifyContent: collapsed ? 'center' : 'flex-start',
                '&.Mui-selected': {
                  bgcolor: mode === 'dark' ? 'rgba(103, 58, 183, 0.2)' : 'rgba(103, 58, 183, 0.08)',
                },
              }}
            >
              <ListItemIcon 
                sx={{ 
                  color: location.pathname === '/user-management' ? 
                    mode === 'dark' ? '#B39DDB' : '#673AB7' : 
                    mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary',
                  minWidth: collapsed ? 'auto' : 32,
                  marginRight: collapsed ? 0 : 2,
                  opacity: location.pathname === '/user-management' ? 1 : 0.7,
                  '& .MuiSvgIcon-root': {
                    fontSize: '1.2rem'
                  }
                }}
              >
                <PeopleIcon />
              </ListItemIcon>
              {!collapsed && (
                <ListItemText 
                  primary="User Management"
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: location.pathname === '/user-management' ? 600 : 500,
                    fontSize: '0.8125rem',
                    color: location.pathname === '/user-management' ? 
                      mode === 'dark' ? '#B39DDB' : '#673AB7' : 
                      mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'text.primary'
                  }}
                />
              )}
            </ListItem>
          </motion.div>
        )}
      </List>
      <Box 
        sx={{ 
          px: collapsed ? 0.5 : 1.5,
          pb: 2,
          borderTop: '1px solid',
          borderColor: 'rgba(0, 0, 0, 0.06)',
          mt: 'auto',
          display: 'flex',
          justifyContent: collapsed ? 'center' : 'flex-start',
        }}
      >
        <Box 
          sx={{ 
            mt: 2,
            borderRadius: 1.5,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
            width: collapsed ? 'auto' : '100%',
          }}
        >
          <Box 
            component={motion.div}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: collapsed ? 'center' : 'flex-start',
              gap: collapsed ? 0 : 1.5, 
              cursor: 'pointer',
              p: 1.25,
            }}
            onClick={handleProfileClick}
          >
            <Avatar 
              sx={{ 
                width: 28, 
                height: 28,
                bgcolor: user?.role === 'admin' 
                  ? '#673AB7' 
                  : user?.role === 'agency' 
                    ? '#009688' 
                    : 'primary.main',
                fontSize: '0.75rem',
                fontWeight: 600
              }}
            >
              {user?.email?.[0]?.toUpperCase()}
            </Avatar>
            {!collapsed && (
              <Box>
                <Typography 
                  variant="body2" 
                  fontWeight={600}
                  color="text.primary"
                  sx={{ 
                    fontSize: '0.8125rem',
                    lineHeight: 1.2 
                  }}
                >
                  {user?.email?.split('@')[0]}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', fontSize: '0.75rem' }}
                >
                  {user?.role || 'User'}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </motion.div>
  );

  // Create the mobile header that shows up when the sidebar is collapsed
  const mobileHeader = isMobile && (
    <AppBar
      position="fixed"
      sx={{
        boxShadow: 'none',
        backgroundColor: mode === 'dark' ? 'background.paper' : '#fff',
        borderBottom: '1px solid',
        borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.06)',
        color: 'text.primary',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{ mr: 2 }}
        >
          <MenuIcon />
        </IconButton>
        <img 
          src={logo} 
          alt="AI Force" 
          style={{ 
            height: '28px',
            objectFit: 'contain',
            filter: 'brightness(0.95)'
          }} 
        />
        <IconButton
          onClick={handleProfileClick}
          size="small"
          sx={{ ml: 2 }}
        >
          <Avatar 
            sx={{ 
              width: 28, 
              height: 28,
              bgcolor: user?.role === 'admin' 
                ? '#673AB7' 
                : user?.role === 'agency' 
                  ? '#009688' 
                  : 'primary.main',
              fontSize: '0.75rem',
              fontWeight: 600
            }}
          >
            {user?.email?.[0]?.toUpperCase()}
          </Avatar>
        </IconButton>
      </Toolbar>
    </AppBar>
  );

  return (
    <>
      {mobileHeader}
      
      {/* Mobile drawer (shown only on mobile) */}
      {isMobile ? (
        <SwipeableDrawer
          variant="temporary"
          open={mobileOpen}
          onOpen={() => setMobileOpen(true)}
          onClose={() => setMobileOpen(false)}
          ModalProps={{
            keepMounted: true, // Better performance on mobile
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              width: 240,
              boxSizing: 'border-box',
              bgcolor: mode === 'dark' ? 'background.paper' : '#fff',
              borderRight: '1px solid',
              borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.06)',
              boxShadow: 'none',
              mt: '56px', // Match AppBar height
              height: 'calc(100% - 56px)' // Subtract AppBar height
            },
          }}
        >
          {drawerContent}
        </SwipeableDrawer>
      ) : (
        // Desktop drawer (permanent)
        <Drawer
          variant="permanent"
          sx={{
            width: collapsed ? 60 : 200,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: collapsed ? 60 : 200,
              boxSizing: 'border-box',
              bgcolor: mode === 'dark' ? 'background.paper' : '#fff',
              borderRight: '1px solid',
              borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.06)',
              boxShadow: 'none',
              display: 'flex',
              flexDirection: 'column',
              transition: 'width 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1.5,
            minWidth: 200,
            borderRadius: 2,
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.08))',
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        <MenuItem sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <ListItemIcon>
            {mode === 'dark' ? (
              <DarkModeIcon fontSize="small" sx={{ color: '#673AB7' }} />
            ) : (
              <LightModeIcon fontSize="small" sx={{ color: '#FFC107' }} />
            )}
          </ListItemIcon>
          <ListItemText 
            primary={mode === 'dark' ? 'Dark Mode' : 'Light Mode'}
            primaryTypographyProps={{
              variant: 'body2',
              fontWeight: 500
            }}
          />
          <Switch
            size="small"
            checked={mode === 'dark'}
            onChange={toggleColorMode}
            color="default"
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: mode === 'dark' ? '#673AB7' : 'primary.main'
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: mode === 'dark' ? '#673AB7' : 'primary.main'
              }
            }}
          />
        </MenuItem>
        {!isMobile && !isTablet && (
          <MenuItem onClick={() => setCollapsed(!collapsed)} sx={{ py: 1 }}>
            <ListItemIcon>
              {collapsed ? (
                <MenuIcon fontSize="small" />
              ) : (
                <ChevronLeftIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText 
              primary={collapsed ? "Expand Sidebar" : "Collapse Sidebar"}
              primaryTypographyProps={{
                variant: 'body2',
                fontWeight: 500
              }}
            />
          </MenuItem>
        )}
        <MenuItem onClick={handleLogout} sx={{ py: 1 }}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText 
            primary="Logout"
            primaryTypographyProps={{
              variant: 'body2',
              fontWeight: 500,
              color: 'error.main'
            }}
          />
        </MenuItem>
      </Menu>
    </>
  );
};

export default Sidebar; 